import { useState, useEffect } from 'react';
import { 
  BarChart3, 
  Download,
  DollarSign,
  Users,
  Gamepad2,
  TrendingUp,
  Percent,
  FileText
} from 'lucide-react';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { Button } from '../components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { reportsAPI } from '../lib/api';
import { formatCurrency, formatNumber, downloadCSV } from '../lib/utils';
import { toast } from 'sonner';

const COLORS = ['#F59E0B', '#10B981', '#3B82F6', '#8B5CF6', '#EF4444'];

export default function Reports() {
  const [financialData, setFinancialData] = useState(null);
  const [userData, setUserData] = useState(null);
  const [gameData, setGameData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('financial');

  const fetchReports = async () => {
    try {
      setLoading(true);
      const [financial, users, games] = await Promise.all([
        reportsAPI.financial(),
        reportsAPI.users(),
        reportsAPI.games(),
      ]);
      setFinancialData(financial.data);
      setUserData(users.data);
      setGameData(games.data);
    } catch (error) {
      toast.error('Failed to fetch reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, []);

  const handleExport = (type) => {
    let data;
    let filename;
    
    switch (type) {
      case 'financial':
        data = financialData ? [financialData] : [];
        filename = 'financial-report';
        break;
      case 'users':
        data = userData ? [userData] : [];
        filename = 'user-report';
        break;
      case 'games':
        data = gameData ? [gameData] : [];
        filename = 'game-report';
        break;
      default:
        return;
    }
    
    if (data.length > 0) {
      downloadCSV(data, filename);
      toast.success('Report exported');
    }
  };

  const revenueChartData = [
    { name: 'Rake', value: financialData?.total_rake || 0 },
    { name: 'GST', value: financialData?.gst_collected || 0 },
    { name: 'TDS', value: financialData?.tds_deducted || 0 },
    { name: 'Net Profit', value: financialData?.net_profit || 0 },
  ];

  const gameChartData = [
    { name: 'Points', value: gameData?.points_rummy_rake || 0 },
    { name: 'Pool', value: gameData?.pool_rummy_rake || 0 },
    { name: 'Deals', value: gameData?.deals_rummy_rake || 0 },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div data-testid="reports-page">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Reports & Analytics</h1>
        <p className="page-subtitle">Comprehensive business intelligence and insights</p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="bg-card border border-border">
          <TabsTrigger value="financial" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <DollarSign className="w-4 h-4 mr-2" />
            Financial
          </TabsTrigger>
          <TabsTrigger value="users" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Users className="w-4 h-4 mr-2" />
            Users
          </TabsTrigger>
          <TabsTrigger value="games" className="data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
            <Gamepad2 className="w-4 h-4 mr-2" />
            Games
          </TabsTrigger>
        </TabsList>

        {/* Financial Reports */}
        <TabsContent value="financial">
          <div className="flex justify-end mb-4">
            <Button onClick={() => handleExport('financial')} variant="outline" data-testid="export-financial-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
          
          <div className="widget-grid mb-6">
            <div className="stat-card">
              <DollarSign className="w-5 h-5 text-primary mb-2" />
              <p className="stat-value">{formatCurrency(financialData?.total_revenue || 0)}</p>
              <p className="stat-label">Total Revenue</p>
            </div>
            <div className="stat-card">
              <TrendingUp className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="stat-value">{formatCurrency(financialData?.total_rake || 0)}</p>
              <p className="stat-label">Total Rake</p>
            </div>
            <div className="stat-card">
              <Percent className="w-5 h-5 text-blue-400 mb-2" />
              <p className="stat-value">{formatCurrency(financialData?.gst_collected || 0)}</p>
              <p className="stat-label">GST Collected</p>
            </div>
            <div className="stat-card">
              <FileText className="w-5 h-5 text-amber-400 mb-2" />
              <p className="stat-value">{formatCurrency(financialData?.tds_deducted || 0)}</p>
              <p className="stat-label">TDS Deducted</p>
            </div>
            <div className="stat-card">
              <TrendingUp className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="stat-value text-emerald-400">{formatCurrency(financialData?.net_profit || 0)}</p>
              <p className="stat-label">Net Profit</p>
            </div>
            <div className="stat-card">
              <DollarSign className="w-5 h-5 text-violet-400 mb-2" />
              <p className="stat-value">{formatCurrency(financialData?.bonus_liability || 0)}</p>
              <p className="stat-label">Bonus Liability</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="chart-container">
              <h3 className="chart-title mb-4">Revenue Breakdown</h3>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={revenueChartData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                  <XAxis dataKey="name" stroke="hsl(var(--muted-foreground))" />
                  <YAxis stroke="hsl(var(--muted-foreground))" tickFormatter={(v) => `${(v / 1000).toFixed(0)}K`} />
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [formatCurrency(value), '']}
                  />
                  <Bar dataKey="value" fill="#F59E0B" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h3 className="chart-title mb-4">Deposit vs Withdrawal</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={[
                      { name: 'Deposits', value: financialData?.deposits || 0 },
                      { name: 'Withdrawals', value: financialData?.withdrawals || 0 },
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    <Cell fill="#10B981" />
                    <Cell fill="#F59E0B" />
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [formatCurrency(value), '']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </div>
        </TabsContent>

        {/* User Reports */}
        <TabsContent value="users">
          <div className="flex justify-end mb-4">
            <Button onClick={() => handleExport('users')} variant="outline" data-testid="export-users-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
          
          <div className="widget-grid mb-6">
            <div className="stat-card">
              <Users className="w-5 h-5 text-primary mb-2" />
              <p className="stat-value">{formatNumber(userData?.total_users || 0)}</p>
              <p className="stat-label">Total Users</p>
            </div>
            <div className="stat-card">
              <TrendingUp className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="stat-value">{formatNumber(userData?.active_users || 0)}</p>
              <p className="stat-label">Active Users</p>
            </div>
            <div className="stat-card">
              <Users className="w-5 h-5 text-blue-400 mb-2" />
              <p className="stat-value">{userData?.new_users_today || 0}</p>
              <p className="stat-label">New Today</p>
            </div>
            <div className="stat-card">
              <DollarSign className="w-5 h-5 text-amber-400 mb-2" />
              <p className="stat-value">{formatCurrency(userData?.ltv || 0)}</p>
              <p className="stat-label">Avg LTV</p>
            </div>
            <div className="stat-card">
              <DollarSign className="w-5 h-5 text-violet-400 mb-2" />
              <p className="stat-value">{formatCurrency(userData?.arpu || 0)}</p>
              <p className="stat-label">ARPU</p>
            </div>
            <div className="stat-card">
              <Percent className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="stat-value">{((userData?.retention_rate || 0) * 100).toFixed(1)}%</p>
              <p className="stat-label">Retention Rate</p>
            </div>
          </div>

          <div className="chart-container">
            <h3 className="chart-title mb-4">User Metrics</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-muted rounded-lg text-center">
                <p className="text-2xl font-bold font-mono">{((userData?.retention_rate || 0) * 100).toFixed(1)}%</p>
                <p className="text-sm text-muted-foreground">Retention</p>
              </div>
              <div className="p-4 bg-muted rounded-lg text-center">
                <p className="text-2xl font-bold font-mono text-red-400">{((userData?.churn_rate || 0) * 100).toFixed(1)}%</p>
                <p className="text-sm text-muted-foreground">Churn</p>
              </div>
              <div className="p-4 bg-muted rounded-lg text-center">
                <p className="text-2xl font-bold font-mono">{formatCurrency(userData?.ltv || 0)}</p>
                <p className="text-sm text-muted-foreground">LTV</p>
              </div>
              <div className="p-4 bg-muted rounded-lg text-center">
                <p className="text-2xl font-bold font-mono">{formatCurrency(userData?.arpu || 0)}</p>
                <p className="text-sm text-muted-foreground">ARPU</p>
              </div>
            </div>
          </div>
        </TabsContent>

        {/* Game Reports */}
        <TabsContent value="games">
          <div className="flex justify-end mb-4">
            <Button onClick={() => handleExport('games')} variant="outline" data-testid="export-games-btn">
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
          
          <div className="widget-grid mb-6">
            <div className="stat-card">
              <Gamepad2 className="w-5 h-5 text-primary mb-2" />
              <p className="stat-value">{formatNumber(gameData?.total_games || 0)}</p>
              <p className="stat-label">Total Games</p>
            </div>
            <div className="stat-card">
              <DollarSign className="w-5 h-5 text-emerald-400 mb-2" />
              <p className="stat-value">{formatCurrency(gameData?.points_rummy_rake || 0)}</p>
              <p className="stat-label">Points Rummy Rake</p>
            </div>
            <div className="stat-card">
              <DollarSign className="w-5 h-5 text-blue-400 mb-2" />
              <p className="stat-value">{formatCurrency(gameData?.pool_rummy_rake || 0)}</p>
              <p className="stat-label">Pool Rummy Rake</p>
            </div>
            <div className="stat-card">
              <DollarSign className="w-5 h-5 text-violet-400 mb-2" />
              <p className="stat-value">{formatCurrency(gameData?.deals_rummy_rake || 0)}</p>
              <p className="stat-label">Deals Rummy Rake</p>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="chart-container">
              <h3 className="chart-title mb-4">Rake by Variant</h3>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={gameChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={100}
                    paddingAngle={5}
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {gameChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip 
                    contentStyle={{ 
                      background: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                    formatter={(value) => [formatCurrency(value), '']}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>

            <div className="chart-container">
              <h3 className="chart-title mb-4">Game Statistics</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span className="text-muted-foreground">Average Game Duration</span>
                  <span className="font-mono font-semibold">{gameData?.avg_game_duration || 0} min</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span className="text-muted-foreground">Abandoned Games</span>
                  <span className="font-mono font-semibold text-red-400">{gameData?.abandoned_games || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-muted rounded-lg">
                  <span className="text-muted-foreground">Total Rake Collected</span>
                  <span className="font-mono font-semibold text-emerald-400">
                    {formatCurrency((gameData?.points_rummy_rake || 0) + (gameData?.pool_rummy_rake || 0) + (gameData?.deals_rummy_rake || 0))}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
